import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../services/authAPI';
import { validateOTP, formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import './styles/Verification.css';

const Verification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check if this is email verification via URL token
  const tokenFromURL = searchParams.get('token');

  // Get data from navigation state
  const email = location.state?.email || '';
  const phone = location.state?.phone || '';
  const message = location.state?.message || '';

  // Determine verification type with proper priority
  const isEmailTokenVerification = !!tokenFromURL;
  const isEmailVerification = !!email && !phone;
  const isPhoneVerification = !!phone && !email;
  const isBothProvided = !!email && !!phone;

  // States
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [tokenVerifying, setTokenVerifying] = useState(false);

  
  
  // Redirect if no email, phone, or token
  useEffect(() => {
    if (!email && !phone && !tokenFromURL) {
      navigate('/register');
    }
  }, [email, phone, tokenFromURL, navigate]);
  
  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);
  
  // Verify email token from URL
  const verifyEmailToken = useCallback(async (token) => {
    setTokenVerifying(true);
    try {
      const response = await authAPI.verify({ token });
      console.log('Email token verification successful:', response.data);
      
      setSuccess('✅ Email verified successfully!');
      setIsVerified(true);
      
      setTimeout(() => {
        navigate('/login', {
        state: { message: 'Email verified successfully! You can now sign in.' }
      });
    }, 3000);
  } catch (error) {
    console.error('Email token verification error:', error);
    setError(formatAPIError(error) || 'Email verification failed. Link may be expired.');
  } finally {
    setTokenVerifying(false);
  }
}, [navigate]);
// Handle email token verification from URL
useEffect(() => {
if (tokenFromURL) {
  verifyEmailToken(tokenFromURL);
}
}, [tokenFromURL, verifyEmailToken]);


  // Handle OTP input (for phone verification)
  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setOtp(value);
    if (error) setError('');
  };

  // Handle OTP submission (for phone verification)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpError = validateOTP(otp);
    if (otpError) {
      setError(otpError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.verify({ phone: phone, otp: otp });
      console.log('Phone verification successful:', response.data);
      setSuccess('✅ Phone verified successfully!');
      setIsVerified(true);
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Phone verified successfully! You can now sign in.' } 
        });
      }, 2000);
    } catch (error) {
      console.error('Phone verification error:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // Handle resend
  const handleResend = async () => {
  setResendLoading(true);
  setError('');
  setSuccess('');

  try {
    let response;

    if (isEmailVerification || isBothProvided) {
      response = await authAPI.resendVerification({ email });
      setSuccess('📧 New verification email sent! Please check your inbox.');
    } else {
      response = await authAPI.resendVerification({ phone });
      setSuccess('📱 New verification code sent to your phone!');
    }

    setResendCooldown(60);
    console.log('Resend response:', response.data);

  } catch (error) {
    console.error('Resend error:', error);
    setError(formatAPIError(error));
  } finally {
    setResendLoading(false);
  }
};


  // Email token verification in progress
  if (isEmailTokenVerification && tokenVerifying) {
    return (
      <>
        <Header />
        <div className="verification-container">
          <div className="verification-card">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <h2 className="loading-title">Verifying Your Email</h2>
              <p className="loading-subtitle">Please wait while we verify your email...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Email token verification completed (success or error)
  if (isEmailTokenVerification && !tokenVerifying) {
    return (
      <>
        <Header />
        <div className="verification-container">
          <div className="verification-card">
            {isVerified ? (
              <div className="success-container">
                <div className="success-icon-wrapper">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h2 className="success-title">{success}</h2>
                <p className="success-subtitle">Redirecting to login...</p>
              </div>
            ) : (
              <div className="error-state">
                <div className="error-icon">
                  <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <h2 className="verification-title">Verification Failed</h2>
                <div className="message-alert message-error">
                  <span>{error}</span>
                </div>
                <div className="verification-footer">
                  <Link to="/register" className="footer-link">
                    Back to Registration
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Email verification (waiting for user to click link)
  if (isEmailVerification || isBothProvided) {
    return (
      <>
        <Header />
        <div className="verification-container">
          <div className="verification-card">
            <div className="verification-header">
              <div className="email-icon">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h2 className="verification-title">Check Your Email</h2>
              <p className="verification-subtitle">
                {message || 'We\'ve sent a verification link to your email address.'}
              </p>
              {email && (
                <div className="verification-target">
                  📧 {email}
                </div>
              )}
            </div>

            {/* Success message */}
            {success && (
              <div className="message-alert message-success">
                <span>{success}</span>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="message-alert message-error">
                <span>{error}</span>
              </div>
            )}

            {/* Resend section */}
            <div className="resend-section">
              <p className="resend-text">Didn't receive the email?</p>
              <button
                onClick={handleResend}
                disabled={resendLoading || resendCooldown > 0}
                className="btn-secondary"
              >
                {resendLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  'Resend Email'
                )}
              </button>
              {resendCooldown > 0 && (
                <p className="resend-countdown">
                  You can request a new email in {resendCooldown} seconds
                </p>
              )}
            </div>

            <div className="verification-footer">
              <Link to="/login" className="footer-link">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Phone verification (requires OTP input)
  if (isPhoneVerification) {
    return (
      <>
        <Header />
        <div className="verification-container">
          <div className="verification-card">
            <div className="verification-header">
              <div className="phone-icon">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
              </div>
              <h2 className="verification-title">Verify Your Phone</h2>
              <p className="verification-subtitle">
                {message || 'Enter the 6-digit code sent to your phone.'}
              </p>
              {phone && (
                <div className="verification-target">
                  📱 {phone}
                </div>
              )}
            </div>

            {/* Success message */}
            {success && (
              <div className="message-alert message-success">
                <span>{success}</span>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="message-alert message-error">
                <span>{error}</span>
              </div>
            )}

            <form className="verification-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="otp" className="form-label">
                  Enter 6-digit verification code
                </label>
                <input
                  id="otp"
                  name="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  required
                  className={`otp-input ${error ? 'error' : ''}`}
                  placeholder="000000"
                  value={otp}
                  onChange={handleOTPChange}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Verifying...
                  </>
                ) : (
                  'Verify Phone'
                )}
              </button>
            </form>

            {/* Resend section */}
            <div className="resend-section">
              <p className="resend-text">Didn't receive the code?</p>
              <button
                onClick={handleResend}
                disabled={resendLoading || resendCooldown > 0}
                className="btn-secondary"
              >
                {resendLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  'Resend Code'
                )}
              </button>
              {resendCooldown > 0 && (
                <p className="resend-countdown">
                  You can request a new code in {resendCooldown} seconds
                </p>
              )}
            </div>

            <div className="verification-footer">
              <Link to="/login" className="footer-link">
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Default fallback
  return (
    <>
      <Header />
      <div className="verification-container">
        <div className="verification-card">
          <div className="verification-header">
            <h2 className="verification-title">Verification Required</h2>
            <p className="verification-subtitle">
              Please complete the verification process to continue.
            </p>
          </div>
          <div className="verification-footer">
            <Link to="/register" className="footer-link">
              Back to Registration
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Verification;
